import type {PropertyValues, TemplateResult} from 'lit';
import {html, LitElement} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {
  mapClick$,
  visibleLayers$,
  edittrackMode$,
} from '../../stores/store-map.ts';
import {
  clearHighlight,
  highlight,
  unhighlight,
} from '../../proxies/proxy-highlight.ts';
import {getOlBBoxFromCoordinates, unselectPois} from '../../map/helpers.ts';
import type {QueryFeatureLayer, QueryItem} from '../../apis/map-api-query.ts';
import {fetchFeatures} from '../../apis/map-api-query.ts';
import {isDetoursEnabled} from '../../urlHandling/urlSearchParams.ts';
import {classMap} from 'lit/directives/class-map.js';
import {closeHeader, spinnerSvg, poiPointSvg} from '../../helpers/elements.ts';
import {scrollingElement} from '../../helpers/dom.ts';
import {QUERY_TOLERANCE} from '../../map/constants.ts';
import {panPopupIntoView} from '../../map/creation.ts';
import type Map from 'ol/Map';
import {Place} from '../../apis/api-places.ts';
import {Sightseeing} from '../../apis/api-sightseeings.ts';
import {modal$} from '../../stores/store-main.ts';
import {
  Bicycle,
  Accommodation,
  Swisspark,
  WildlifeArea,
  AlpguardDog,
  WildlifeRoute,
  Station,
  Slowup,
  Municipality,
  Canton,
  Shooting,
} from '../../apis/api-poi.ts';
import type {Subscription} from 'rxjs';
import {PhotoLayer} from '../../map/layers/photo.ts';
import {Feature} from 'ol';
import {Detour} from '../../apis/api-goodtoknow.ts';
import {LAYER_ORDER, ROUTE_LAYERS} from '../../layers/layerConstants.ts';
import {WS_I18N} from '../../layers/layerTranslations.ts';
import type {
  LandCode,
  PoiTranslationKey,
  LayerCode,
  QueryCategory,
} from '../../layers/layerTypes.ts';
import {fetchTourAndPois} from '../../apis/api-tours.ts';
import type {HttpError} from '../../apis/api-tours.ts';
import {msg} from '@lit/localize';
import type VectorLayer from 'ol/layer/Vector.js';
import GeometryCollection from 'ol/geom/GeometryCollection.js';
import {handleError} from '../../helpers/errorHandling.ts';
import type VectorSource from 'ol/source/Vector';
import {TIME_TYPES} from '../../helpers/tours.ts';

const LAND_COLORS_CLASS: Record<LandCode, string> = {
  cycle: 'bg-page-cycle',
  hike: 'bg-page-hike',
  mountainbike: 'bg-page-mountainbike',
  skate: 'bg-page-skate',
  canoe: 'bg-page-canoe',
  crosscountry: 'bg-page-crosscountry',
  winterhike: 'bg-page-winterhike',
  snowshoe: 'bg-page-snowshoe',
  sledge: 'bg-page-sledge',
};

const SKIP_CATEGORIES = ['detour'];

@customElement('element-shortlist')
export default class ElementShortlist extends LitElement {
  @property({type: Object}) map: Map;
  @state() featuresTemplate: TemplateResult<1> | undefined;
  @state() show = false;
  private closeTimeout: NodeJS.Timeout | undefined;
  private mapClick: Subscription;
  private edittrackModeSubscription: Subscription;
  private clicked = false;
  @state() private isScrolled = false;
  private groupedFeatures: undefined | [string, QueryItem[]][];

  edittrackMode = edittrackMode$.getValue();

  orderFeatures(features: QueryItem[]) {
    const layerIndex = (key: string) => {
      if (key === 'tour' || key === 'poi') {
        // special case for tour, always at the bottom of the shortlist
        return Number.POSITIVE_INFINITY;
      }

      for (let i = 0, ii = LAYER_ORDER.length; i < ii; i++) {
        if (key.match(LAYER_ORDER[i])) {
          return i;
        }
      }
      return Number.POSITIVE_INFINITY;
    };

    features.sort((a, b) => {
      const indexDiff = layerIndex(a.layer) - layerIndex(b.layer);
      if (indexDiff === 0) {
        return a.title > b.title ? 1 : a.title < b.title ? -1 : 0;
      } else {
        return indexDiff;
      }
    });
    return features;
  }

  filterFeaturesByGeometry(features: QueryItem[]) {
    const pointFeatures = features.filter(
      (feature) => feature.geometryType === 'Point',
    );
    if (pointFeatures.length > 0) {
      // only display the 'Point' features
      return pointFeatures;
    }

    if (this.map.getView().getResolution() > 20) {
      // Show query results for the lines only for resolutions 1-20
      features = features.filter((feature) => {
        return !(
          feature.geometryType?.includes('Line') && feature.type === 'wildlife'
        );
      });
    }

    // select only line on polygon-line click
    const notPolygonFeatures = features.filter(
      (feature) => !feature.geometryType?.includes('Polygon'),
    );
    if (notPolygonFeatures.length > 0) {
      features = notPolygonFeatures;
    }

    return features;
  }

  // group features by layer name
  groupFeatures(features: QueryItem[]) {
    const grouped: Partial<Record<QueryFeatureLayer, QueryItem[]>> = {};
    features.forEach((f) => {
      const layer = <QueryFeatureLayer>(
        (f.layer.includes('.')
          ? f.layer.substring(0, f.layer.indexOf('.'))
          : f.layer)
      );
      if (f.type === 'wildlife' && f.subtype) {
        const subtype = f.subtype;
        if (!grouped[subtype]) grouped[subtype] = [];
        grouped[subtype].push(f);
      } else if (f.category === 'accommodation') {
        if (!grouped[f.category]) grouped[f.category] = [];
        grouped[f.category].push(f);
      } else {
        if (!grouped[layer]) grouped[layer] = [];
        grouped[layer].push(f);
      }
    });
    return grouped;
  }

  getShortlistContent(rawFeatures: QueryItem[]) {
    let features: QueryItem[] = [];
    const filteredFeatures: Record<
      (typeof SKIP_CATEGORIES)[number],
      QueryItem[]
    > = {};

    rawFeatures.forEach((feature) => {
      if (!SKIP_CATEGORIES.includes(feature.category)) {
        features.push(feature);
      } else {
        if (!filteredFeatures[feature.category])
          filteredFeatures[feature.category] = [];
        filteredFeatures[feature.category].push(feature);
      }
    });
    const vLayers = visibleLayers$.getValue();
    features = features.filter(
      (f) =>
        (f.category === 'accommodation' && vLayers.includes('accommodation')) ||
        (f.category === 'station' && vLayers.includes('station')) ||
        f.layers.some((l) => vLayers.some((vl) => vl && l.includes(vl))) ||
        f.category === 'tour' ||
        f.category === 'poi' ||
        f.layer === 'shooting',
    );
    features = this.orderFeatures(features);

    // Extend menu with detour features
    if (filteredFeatures['detour'] && isDetoursEnabled()) {
      features.unshift(...filteredFeatures['detour']);
    }

    features = this.filterFeaturesByGeometry(features);

    return this.groupFeatures(features);
  }

  close() {
    this.featuresTemplate = undefined;
    this.show = false;
  }

  async chooseItem(item: QueryItem) {
    if (item.layer === 'tour') {
      unhighlight(item.olFeature);
      if (location.pathname.includes('tour/') && item.category === 'poi') {
        // if the user is already on a tour page, don't navigate to another tour
        unselectPois(this.map);
        this.dispatchEvent(
          new CustomEvent('clickPoi', {detail: item.olFeature, bubbles: true}),
        );
        this.show = false;
        return;
      }
      this.dispatchEvent(
        new CustomEvent('clickTour', {
          detail: {
            tourId: item.tourId,
            type: item.type,
            poiId: item.poiId,
          },
          // As this element is part of the map, the event is not treated by the parent,
          // but needs to bubble to the 'element-map'.
          bubbles: true,
        }),
      );
      this.show = false;
      return;
    }
    highlight(item.olFeature, false);
    if (item.routeNumber) {
      this.dispatchEvent(
        new CustomEvent('clickRouteOrSegment', {
          detail: {
            routeNumber: item.routeNumber,
            segment: item.segmentNumber,
            land: item.land,
          },
          // As this element is part of the map, the event is not treated by the parent,
          // but needs to bubble to the 'element-map'.
          bubbles: true,
        }),
      );
    } else if (item.layer === 'place') {
      modal$.next({type: 'place', data: new Place(item.olFeature)});
    } else if (item.layer === 'sightseeing') {
      modal$.next({
        type: 'sightseeing',
        data: new Sightseeing(item.olFeature),
      });
    } else if (item.layer === 'velorental' || item.layer === 'cycleservice') {
      modal$.next({type: 'details', data: new Bicycle(item.olFeature)});
    } else if (item.category === 'accommodation') {
      modal$.next({
        type: 'details',
        data: new Accommodation(item.olFeature),
      });
    } else if (item.category === 'detour') {
      modal$.next({type: 'details', data: new Detour(item.olFeature)});
    } else if (item.category === 'swisspark') {
      modal$.next({
        type: 'details',
        data: new Swisspark(item.olFeature),
      });
    } else if (item.type === 'wildlife') {
      item.olFeature.set('subtitle', item.subtitle);
      if (item.geometryType === 'LineString') {
        modal$.next({
          type: 'details',
          data: new WildlifeRoute(item.olFeature),
        });
      } else {
        modal$.next({
          type: 'details',
          data: new WildlifeArea(item.olFeature),
        });
      }
    } else if (item.layer === 'alpguarddog') {
      modal$.next({
        type: 'details',
        data: new AlpguardDog(item.olFeature),
      });
    } else if (item.category === 'station') {
      modal$.next({type: 'details', data: new Station(item.olFeature)});
    } else if (item.category === 'slowup') {
      modal$.next({type: 'details', data: new Slowup(item.olFeature)});
    } else if (item.layer === 'municipalities') {
      modal$.next({
        type: 'details',
        data: new Municipality(item.olFeature),
      });
    } else if (item.layer === 'cantons') {
      modal$.next({
        type: 'details',
        data: new Canton(item.olFeature),
      });
    } else if (item.layer === 'shooting') {
      modal$.next({
        type: 'details',
        data: new Shooting(item.olFeature),
      });
    }
    this.show = false;
  }

  renderShortListContent(groupedFeatures: [string, QueryItem[]][]) {
    // map height minus title, arrow and gap
    const maxHeight = this.map.getSize()[1] - 80;
    return html`
      <div class="shortlist-content bg-white shadow-popup">
        ${closeHeader(null, this.close, this.isScrolled)}
        <div
          class="w-[324px] divide-y divide-primary-gray overflow-y-auto"
          style="max-height: ${maxHeight}px"
          @scroll="${(event: Event) =>
            (this.isScrolled = scrollingElement(event))}"
        >
          ${groupedFeatures.map(([key, items]) => {
            const slowupColor = key === 'slowup' ? 'bg-slowup' : undefined;
            const tourColor =
              key === 'tour' || key === 'poi' ? 'bg-white' : undefined;
            const titleColor =
              slowupColor ||
              tourColor ||
              LAND_COLORS_CLASS[<LandCode>key] ||
              'bg-page-main';
            const title =
              key in ROUTE_LAYERS
                ? ROUTE_LAYERS[<LandCode>key].getFlatTitle()
                : key === 'tour'
                  ? msg('My tour')
                  : WS_I18N[<PoiTranslationKey>key];
            const divide =
              key === 'tour' ? 'divide-chm-light-gray' : 'divide-white';
            return html`
              <div>
                <div
                  class="${titleColor} h-[40px] px-[12px] py-[9px] text-shortlist-t"
                >
                  ${title}
                </div>
                <ul class="divide-y ${divide}">
                  <li></li>
                  ${items.map((feature: QueryItem) => {
                    const landColor =
                      feature.category === 'slowup'
                        ? 'bg-slowup'
                        : feature.category === 'tour' ||
                            feature.category === 'poi'
                          ? 'bg-white'
                          : LAND_COLORS_CLASS[feature.land];
                    const color =
                      !landColor || feature.category === 'detour'
                        ? 'bg-page-main'
                        : landColor;
                    return html`
                      <li
                        class="${classMap({
                          [color]: true,
                          'items-center':
                            !feature.subtitle || feature.subtitle === '',
                        })} flex min-h-[56px] cursor-pointer gap-x-[8px] p-[12px] mouse:hover:bg-white"
                        @click="${() => {
                          this.clicked = true;
                          this.chooseItem(feature);
                        }}"
                        @mouseover="${() => {
                          if (!this.clicked) {
                            highlight(feature.olFeature, false);
                          }
                        }}"
                        @mouseout="${() => {
                          if (!this.clicked) {
                            unhighlight(feature.olFeature);
                          }
                        }}"
                      >
                        ${feature.logoSrc
                          ? html`<img
                              class="h-[24px] w-[24px]"
                              src="${feature.logoSrc}"
                            />`
                          : ''}
                        ${feature.category === 'tour'
                          ? html`<div
                              class="w-[24px] h-[24px] bg-[auto_24px] shrink-0 ${classMap(
                                {
                                  [TIME_TYPES[feature.timeType]]:
                                    !!feature.timeType &&
                                    feature.type === 'normal',
                                  'bg-recorded-icon': feature.type !== 'normal',
                                },
                              )}"
                            ></div>`
                          : ''}
                        ${feature.category === 'poi'
                          ? html`<div>
                              ${poiPointSvg({
                                classes: 'w-[24px] h-[33px]',
                                fill: feature.colorRgb,
                                number: feature.poiIndex + 1,
                              })}
                            </div>`
                          : ''}
                        <div>
                          <p
                            class="-mb-[2px] -mt-[4px] text-shortlist-result-t font-bold"
                          >
                            ${feature.title}
                          </p>
                          <p class="text-shortlist-result">
                            ${feature.subtitle}
                          </p>
                        </div>
                      </li>
                    `;
                  })}
                </ul>
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  async updateFeatures(coords: number[]) {
    const resolution = this.map.getView().getResolution();
    const bbox = getOlBBoxFromCoordinates(
      coords,
      QUERY_TOLERANCE.default,
      resolution,
    );
    const wildlifeToleranceBbox = getOlBBoxFromCoordinates(
      coords,
      QUERY_TOLERANCE.wildlife,
      resolution,
    );
    let layers: string[] = [];
    this.map.getLayers().forEach((l) => {
      const props = l.getProperties();
      if (l.getVisible() && props['name']) layers.push(props['name']);
    });
    let trackItem;
    let poiTourItem;
    const trackLayerVisible = layers.includes('trackLayer');
    layers = layers.filter((layer) => layer !== 'trackLayer');
    const landLayers = layers as LayerCode[];
    const features: QueryItem[] = await fetchFeatures(
      landLayers,
      bbox,
      wildlifeToleranceBbox,
    );

    if (trackLayerVisible) {
      const mapFeatures = this.map.getFeaturesAtPixel(
        this.map.getPixelFromCoordinate(coords),
        {
          hitTolerance: QUERY_TOLERANCE.default,
        },
      );

      const tourFeature = mapFeatures.filter(
        (feature) =>
          feature.get('tourId') !== undefined && feature.get('type') !== 'POI',
      )[0];

      const poiFeatures = mapFeatures.filter(
        (feature) => feature.get('type') === 'POI',
      );

      // If click is on one POI, open the POI view and don't open the shortlist
      if (poiFeatures.length === 1 && !features.length && !tourFeature) {
        const poiFeature = poiFeatures[0] as Feature;
        unselectPois(this.map);
        this.dispatchEvent(
          new CustomEvent('clickPoi', {detail: poiFeature, bubbles: true}),
        );
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout);
          this.closeTimeout = undefined;
        }
        this.featuresTemplate = html``;
        return;
      }

      if (tourFeature || poiFeatures.length) {
        const trackLayer = this.map
          .getLayers()
          .getArray()
          .filter((layer) => layer.get('name') === 'trackLayer');

        const allFeaturesOfTrackLayer = (
          trackLayer[0] as VectorLayer<VectorSource>
        )
          .getSource()
          .getFeatures();

        const combinedFeature = new Feature({
          geometry: new GeometryCollection(
            allFeaturesOfTrackLayer.map((feature) => feature.getGeometry()),
          ),
        });
        try {
          const tourId =
            tourFeature?.get('tourId') || poiFeatures[0].get('tourId');
          const tourType =
            tourFeature?.get('tourType') || poiFeatures[0].get('tourType');

          const trackFeature = await fetchTourAndPois(tourId, tourType);

          if (tourFeature) {
            trackItem = {
              title: trackFeature.tour.properties.name,
              type: trackFeature.tour.properties.type,
              timeType: trackFeature.tour.properties.pref.timetype,
              layer: 'tour' as QueryFeatureLayer,
              layers: ['trackLayer' as LayerCode],
              subtitle: '',
              category: 'tour' as QueryCategory,
              olFeature: combinedFeature,
              tourId: trackFeature.tour.id,
              poiId:
                tourFeature.get('type') === 'POI'
                  ? typeof tourFeature.getId() === 'string'
                    ? (parseInt(tourFeature.getId() as string) as number)
                    : (tourFeature.getId() as number)
                  : undefined,
              colorRgb: trackFeature.tour.properties.pref.colorRgb,
            };
            features.push(trackItem);
          }
          if (poiFeatures.length) {
            poiFeatures.forEach((feature) => {
              poiTourItem = {
                title: feature.get('meta').title,
                type: trackFeature.tour.properties.type,
                layer: 'tour' as QueryFeatureLayer,
                layers: ['trackLayer' as LayerCode],
                subtitle: '',
                category: 'poi' as QueryCategory,
                olFeature: feature as Feature,
                tourId: trackFeature.tour.id,
                poiId: parseInt(feature.getId() as string) as number,
                poiIndex: parseInt(feature.get('index')),
                colorRgb: trackFeature.tour.properties.pref.colorRgb,
              };
              features.push(poiTourItem);
            });
          }
        } catch (e) {
          handleError(e as HttpError, this.dispatchEvent.bind(this));
        }
      }
    }
    this.groupedFeatures = Object.entries(this.getShortlistContent(features));
    if (!this.groupedFeatures.length) {
      if (this.closeTimeout) clearTimeout(this.closeTimeout);
      this.closeTimeout = setTimeout(() => {
        this.close();
        this.closeTimeout = undefined;
      }, 3000);
      return;
    }
    if (
      this.groupedFeatures.length === 1 &&
      this.groupedFeatures[0][1].length === 1
    ) {
      await this.chooseItem(this.groupedFeatures[0][1][0]);
      this.featuresTemplate = html``;
    } else {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = undefined;
      }
      this.featuresTemplate = this.renderShortListContent(this.groupedFeatures);
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    this.edittrackModeSubscription = edittrackMode$.subscribe(
      (mode) => (this.edittrackMode = mode),
    );

    this.mapClick = mapClick$.subscribe((coords) => {
      modal$.next(undefined);
      clearHighlight(false);
      this.clicked = false;
      this.featuresTemplate = undefined;
      if (this.edittrackMode === 'edit') {
        // Disable query when edittrack is active
        return;
      }
      const photoFeatures = this.map.getFeaturesAtPixel(
        this.map.getPixelFromCoordinate(coords),
        {
          layerFilter: (layer) => layer instanceof PhotoLayer,
          hitTolerance: QUERY_TOLERANCE.photo,
        },
      );
      if (photoFeatures.length) {
        const features = photoFeatures[0].get('features');
        const photos = features.map((feature: Feature) => {
          const props = feature.getProperties();
          return {
            src: props['photo'],
            srcBig: props['photo'],
            urls: props['urls'],
            copyright: props['copyright'],
          };
        });
        modal$.next({
          type: 'gallery',
          data: {photos, index: 0},
        });
      } else {
        clearHighlight(false);
        this.clicked = false;
        this.updateFeatures(coords);
        this.show = true;
      }
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.mapClick.unsubscribe();
    this.edittrackModeSubscription.unsubscribe();
  }

  override updated(_changedProperties: PropertyValues) {
    panPopupIntoView('shortlist');

    // Force re-render the template if scroll need to change the header style
    _changedProperties.forEach((value, key) => {
      if (key === 'isScrolled' && value !== undefined) {
        this.featuresTemplate = this.renderShortListContent(
          this.groupedFeatures,
        );
      }
    });
  }

  override render() {
    this.style.zIndex = '50';
    this.style.position = 'absolute';
    this.style.transform = this.featuresTemplate
      ? 'translate(-30px, calc(-100% - 24px))'
      : 'translateY(-100%)';
    return html` <div
      class="${classMap({
        hidden: !this.show,
      })}"
    >
      ${this.featuresTemplate ||
      spinnerSvg('w-6 h-6 text-chm-gray fill-chm-red')}
    </div>`;
  }

  override createRenderRoot() {
    return this;
  }
}
